/**
 * The Settings tab vocabulary — shared by the routed page (`SettingsScreen`) and the modal
 * (`SettingsModal`) so the two chromes can never disagree about which tabs exist, their order, or who
 * is allowed to see one.
 */
export type SettingsTab = "account" | "profile" | "features" | "connections" | "team" | "tutorials";

export interface SettingsTabDef {
  key: SettingsTab;
  label: string;
  /** Capability required to see the tab; undefined ⇒ every seat. */
  cap?: string;
  /** Hidden from every seat. Temporary: Features is pulled until its content is ready (PO, 2026-10-12). */
  hidden?: boolean;
}

// The Connections tab is surfaced only to seats that can manage hookups — the band's own AV/TM seats
// (`connections:manage`), mirroring the server NavigationDocument; it is HIDDEN for the silo roles
// (content/merch/production/band_member/booking) and for org-lens/impersonator sessions.
// The connection-WRITE endpoints are `[RequiresCapability(connections:manage)]` server-side, so this is UI hygiene
// over a backend deny-by-default, never the sole gate.
/**
 * Order and labels are the spec's, left to right: Artist Profile · Connections · Team · Account · Tutorials.
 *
 * Both band-wide tabs gate on **`band:manage`**, not `connections:manage`. The server grants
 * `band:manage` to the AV-owner seat ALONE ("never via org reach or platform:admin"), which is exactly
 * the spec's "only the AV / primary seat manages band-wide settings". `connections:manage` is
 * deliberately wider — AV **or** Tour Manager — so gating on it let a TM see both tabs.
 *
 * Every other Artist-View seat (Band Member, Tour Manager, Production, Merch, Content) therefore sees
 * Account + Features only, and the modal opens on the first tab the seat can actually see.
 */
export const SETTINGS_TABS: readonly SettingsTabDef[] = [
  { key: "profile", label: "Artist Profile", cap: "band:manage" },
  { key: "connections", label: "Connections", cap: "band:manage" },
  // ART-353: the band's partner choices (manager mode, label budgets, booking discovery, Advance Docs Pro).
  { key: "team", label: "Team", cap: "band:manage" },
  { key: "account", label: "Account" },
  { key: "features", label: "Features", hidden: true },
  // ART-293: the admin console's how-to videos. Every seat; shown only when the list has something in it.
  { key: "tutorials", label: "Tutorials" },
];

/**
 * The tabs a seat may see, in order. Both chromes filter through this so neither can drift.
 *
 * `hideConnections` drops the Connections tab on the MOBILE shell (spec §3: "Connections stays
 * desktop-primary" — the connect popups + vendor consoles are a desktop workflow). It is UI hygiene over the
 * server's `[RequiresCapability(connections:manage)]` deny-by-default, and only the routed `SettingsScreen`
 * passes it (the modal is desktop-only), so a deep link on desktop still shows the tab (ART-153 pass two).
 *
 * `hasTutorials` is whether this seat's tutorials list has any rows (ART-293): an empty Tutorials tab is a tab
 * with nothing behind it, so it is left out until the admin adds one for this audience.
 */
export function visibleSettingsTabs(
  canManageBand: boolean,
  opts?: { hideConnections?: boolean; hasTutorials?: boolean },
): readonly SettingsTabDef[] {
  return SETTINGS_TABS.filter(
    (t) =>
      !t.hidden &&
      (t.cap !== "band:manage" || canManageBand) &&
      !(opts?.hideConnections && t.key === "connections") &&
      !(t.key === "tutorials" && !opts?.hasTutorials),
  );
}
